import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CheckCircle, Circle, User, ArrowRight, X } from 'lucide-react';

const REQUIRED_FIELDS = [
  { key: 'role', label: 'Job Title' },
  { key: 'organisation', label: 'Company / Organisation' },
  { key: 'location', label: 'Location' },
  { key: 'sector', label: 'Sector' },
  { key: 'background', label: 'Stakeholder Type' },
  { key: 'bio', label: 'About Me' },
  { key: 'country', label: 'Country' },
  { key: 'self_descriptions', label: 'How Would You Describe Yourself' },
  // The four behavioural questions are answered together on the profile
  // form, so they count as a single "Behavioural Assessment" unit in the
  // completion calculation (kept in sync with backend `/api/members/xp`).
  { key: 'behavioural_assessment', label: 'Behavioural Assessment' },
];

const BEHAVIOURAL_FIELDS = [
  'behavioural_decisions',
  'behavioural_processing',
  'behavioural_working',
  'behavioural_collaboration',
];

export default function ProfileCompletionBanner({ user }) {
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const profile = user?.profile || {};
  const missing = REQUIRED_FIELDS.filter(f => {
    if (f.key === 'behavioural_assessment') {
      // All four behavioural answers must be set for the assessment to be considered done.
      return !BEHAVIOURAL_FIELDS.every(bf => !!profile[bf]);
    }
    const v = profile[f.key];
    if (f.key === 'self_descriptions') {
      const arr = Array.isArray(v) ? v : (profile.self_description ? [profile.self_description] : []);
      return arr.length === 0;
    }
    if (Array.isArray(v)) return v.length === 0;
    if (typeof v === 'string') return !v.trim();
    return !v;
  });
  const completed = REQUIRED_FIELDS.length - missing.length;
  const pct = Math.round((completed / REQUIRED_FIELDS.length) * 100);
  const isComplete = missing.length === 0;

  useEffect(() => {
    if (isComplete || dismissed || location.pathname === '/profile') return;
    const dismissKey = `profile_reminder_${user?.user_id}`;
    const lastDismissed = sessionStorage.getItem(dismissKey);
    if (lastDismissed) { setDismissed(true); return; }
    const timer = setTimeout(() => setOpen(true), 1500);
    return () => clearTimeout(timer);
  }, [user, location.pathname, isComplete, dismissed]);

  const handleDismiss = () => {
    setOpen(false);
    setDismissed(true);
    sessionStorage.setItem(`profile_reminder_${user?.user_id}`, 'true');
  };

  const handleGoToProfile = () => {
    setOpen(false);
    navigate('/profile');
  };

  if (isComplete || location.pathname === '/profile') return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleDismiss(); }}>
      <DialogContent className="max-w-md" data-testid="profile-completion-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <User className="h-5 w-5 text-brand-navy" /> Complete Your Profile
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-slate-500 -mt-1">
          Help the community get to know you better. Complete your profile to unlock smarter networking and recommendations.
        </p>

        {/* Progress bar */}
        <div className="mt-3">
          <div className="flex items-center justify-between text-xs mb-1.5">
            <span className="font-medium text-slate-600">{pct}% complete</span>
            <span className="text-slate-400">{missing.length} field{missing.length !== 1 ? 's' : ''} remaining</span>
          </div>
          <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-brand-navy to-brand-sage transition-all duration-500"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        {/* Missing fields list */}
        <div className="mt-3 max-h-48 overflow-y-auto space-y-1.5" data-testid="missing-fields-list">
          {REQUIRED_FIELDS.map(f => {
            const done = !missing.find(m => m.key === f.key);
            return (
              <div key={f.key} className="flex items-center gap-2.5 text-sm">
                {done ? (
                  <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                ) : (
                  <Circle className="h-4 w-4 text-slate-300 flex-shrink-0" />
                )}
                <span className={done ? 'text-slate-400 line-through' : 'text-slate-700'}>{f.label}</span>
              </div>
            );
          })}
        </div>

        <div className="flex gap-2 mt-4">
          <Button onClick={handleGoToProfile} className="flex-1 bg-brand-navy hover:bg-brand-dark text-white" data-testid="go-to-profile-btn">
            Complete Now <ArrowRight className="h-4 w-4 ml-1.5" />
          </Button>
          <Button variant="outline" onClick={handleDismiss} data-testid="dismiss-reminder-btn">
            Later
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
